// Modal con el formulario de actualización de datos del operario
// Este componente encapsula el formulario en un IonModal y ofrece acciones de
// Guardar, Más tarde (snooze) y Cerrar. Está pensado para ser invocado desde Home.

import React, { useEffect, useMemo, useState, useRef } from 'react'; // Importamos React y hooks para estado/memoización, incluimos useEffect, y useRef para manejar foco/scroll
import { IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonContent, IonList, IonItem, IonLabel, IonSelect, IonSelectOption, IonInput, IonGrid, IonRow, IonCol, IonNote, IonText, IonChip, IonSpinner, IonAlert } from '@ionic/react'; // Importamos componentes de Ionic, agregamos IonSpinner para indicador de carga e IonAlert para mensajes en estilo de la app
import './ActualizarDatosModal.css'; // Estilos específicos del modal
// Eliminado catálogo local de ciudades/barrios: ahora ciudad y barrio son texto libre
import { getBestPosition, reverseGeocode } from 'src/utils/geo'; // Usamos getBestPosition para mejor precisión inicial
import { putPerfilOperario, getPerfilOperario, validarUbicacionPerfil } from 'src/services/operarioPerfil'; // Servicios para persistir y obtener perfil del backend (incluye validación de ubicación)
import { PerfilOperario, getPerfil, savePerfil } from 'src/services/operarioLocal'; // Importamos servicio local de perfil
import { formatPyLocal, isValidPyPhone } from 'src/utils/phone'; // Utilidades de teléfono PY

// helper para validar formato básico de email
const isValidEmail = (v: string): boolean => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(String(v || '').trim()); // validar email con regex simple

// Props necesarias para operar el modal desde el padre (Home)
type Props = {
  // Control externo de visibilidad del modal
  isOpen: boolean;
  // Handler para cerrar el modal sin guardar
  onClose: () => void;
  // Handler para indicar que se guardó y que el padre puede re-evaluar estado
  onSaved: () => void;
  // Si es true, el modal no se puede cerrar sin guardar (oculta botón "Cerrar").
  // Se usa cuando la campaña obliga al operario a actualizar sus datos.
  forzado?: boolean;
};

// Componente principal
const ActualizarDatosModal: React.FC<Props> = ({ isOpen, onClose, onSaved, forzado = false }) => {
  // Cargamos estado inicial del perfil desde localStorage al montar
  const inicial: PerfilOperario = useMemo(() => getPerfil(), []); // Se calcula una sola vez; la recarga al abrir se maneja con useEffect
  // Estado controlado del formulario
  const [perfil, setPerfil] = useState<PerfilOperario>(inicial);
  // Estados para validación y carga de geolocalización
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadingGeo, setLoadingGeo] = useState(false);
  // Alerta interna estilo Ionic para mensajes de ubicación rechazada por proximidad a un PS
  // (evita usar el alert() del navegador que rompe el look-and-feel).
  const [psAlertOpen, setPsAlertOpen] = useState(false);
  const [psAlertMensaje, setPsAlertMensaje] = useState('');
  // Ya no usamos catálogos locales; ciudad y barrio son texto libre

  // Refs para auto-scroll y auto-focus: guardamos referencias a contenedores e inputs
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({}); // Mapa de refs por nombre de campo para scrollIntoView
  const refEmailPersonal = useRef<HTMLIonInputElement | null>(null); // ref del input de email personal para setFocus()
  const refContactoNombre = useRef<HTMLIonInputElement | null>(null); // Ref del input de contactoNombre para setFocus()
  const refContactoTelefono = useRef<HTMLIonInputElement | null>(null); // Ref del input de contactoTelefono para setFocus()
  const refDireccion = useRef<HTMLIonInputElement | null>(null); // Ref del input de dirección para setFocus()
  const refLatitud = useRef<HTMLIonInputElement | null>(null); // Ref del input de latitud para setFocus()
  const refLongitud = useRef<HTMLIonInputElement | null>(null); // Ref del input de longitud para setFocus()
  const refCiudad = useRef<HTMLIonInputElement | null>(null); // Ref del input de ciudad para setFocus()
  const refBarrio = useRef<HTMLIonInputElement | null>(null); // Ref del input de barrio para setFocus()

  // Helper para enfocar y scrollear al campo con error
  const focusAndScrollTo = (key: string) => { // Recibe la clave del campo a enfocar
    const container = fieldRefs.current[key]; // Obtenemos el contenedor asociado
    if (container && typeof container.scrollIntoView === 'function') { // Verificamos método
      container.scrollIntoView({ behavior: 'smooth', block: 'center' }); // Hacemos scroll suave al centro
    }
    // Mapa de refs de inputs que soportan setFocus()
    const inputMap: Record<string, React.RefObject<HTMLIonInputElement>> = { // Definimos mapa de claves a refs
      emailPersonal: refEmailPersonal, // soporte de foco para email
      contactoNombre: refContactoNombre,
      contactoTelefono: refContactoTelefono,
      direccion: refDireccion,
      latitud: refLatitud,
      longitud: refLongitud,
      ciudad: refCiudad,
      barrio: refBarrio,
    };
    const inputRef = inputMap[key]; // Obtenemos la ref del input (si aplica)
    if (inputRef && inputRef.current && typeof (inputRef.current as any).setFocus === 'function') { // Validamos existencia de setFocus
      setTimeout(() => (inputRef.current as any).setFocus(), 100); // Llamamos a setFocus con un pequeño delay para asegurar render
    }
  };

  // Cuando el modal se abre, consultamos el backend; si no hay datos, mostramos vacío
  useEffect(() => {
    if (isOpen) {
      (async () => {
        try {
          const { perfil: remoto } = await getPerfilOperario(); // Pedimos el último perfil al backend
          if (remoto) {
            // Convertimos tipos a los usados por el formulario y normalizamos movilidad a array
            const movilidadArr: string[] = remoto.movilidad ? String(remoto.movilidad).split(',').map(s => s.trim()).filter(Boolean) : [];
            const nuevo: PerfilOperario = {
              // mapear correo electrónico personal recibido del backend
              emailPersonal: remoto.emailPersonal || '',
              escolaridad: remoto.escolaridad || '',
              ciudad: remoto.ciudad || '',
              barrio: remoto.barrio || '',
              direccion: remoto.direccion || '',
              latitud: remoto.latitud != null ? String(remoto.latitud) : '',
              longitud: remoto.longitud != null ? String(remoto.longitud) : '',
              contactoNombre: remoto.contactoNombre || '',
              contactoTelefono: remoto.contactoTelefono || '',
              movilidad: movilidadArr,
            };
            setPerfil(nuevo);
          } else {
            // No hay registro: limpiamos el formulario
            setPerfil({});
          }
        } catch {
          // En caso de error en backend, evitamos prellenar con localStorage
          setPerfil({});
        } finally {
          setErrors({});
        }
      })();
    }
  }, [isOpen]); // Dispara cuando cambia la visibilidad del modal

  // Actualiza cualquier campo del perfil de manera genérica por clave
  const onChange = (campo: keyof PerfilOperario, valor: string) => {
    // Si cambia ciudad, reseteamos barrio
    if (campo === 'ciudad') {
      setPerfil(prev => ({ ...prev, ciudad: valor, barrio: '' }));
    } else {
      setPerfil(prev => ({ ...prev, [campo]: valor }));
    }
    setErrors(prev => ({ ...prev, [campo]: '' }));
  };

  // Valida campos requeridos y retorna si es válido
  const validar = (): boolean => {
    const nuevo: Record<string, string> = {};
    // validar email personal como requerido y formato
    if (!perfil.emailPersonal) {
      nuevo.emailPersonal = 'Requerido';
    } else if (!isValidEmail(String(perfil.emailPersonal))) {
      nuevo.emailPersonal = 'Formato inválido';
    }
    if (!perfil.escolaridad) nuevo.escolaridad = 'Requerido';
    // Ciudad/barrio: requerimos selección del listado
    if (!perfil.ciudad) nuevo.ciudad = 'Requerido';
    if (!perfil.barrio) nuevo.barrio = 'Requerido';
    if (!perfil.direccion) nuevo.direccion = 'Requerido';
    if (!perfil.latitud) nuevo.latitud = 'Requerido';
    if (!perfil.longitud) nuevo.longitud = 'Requerido';
    if (!perfil.contactoNombre) nuevo.contactoNombre = 'Requerido';
    if (!perfil.contactoTelefono) {
      nuevo.contactoTelefono = 'Requerido';
    } else if (!isValidPyPhone(`+595 ${perfil.contactoTelefono}`)) {
      nuevo.contactoTelefono = 'Formato inválido. Use +595 …';
    }
    // Validación de movilidad: al menos una opción
    if (!perfil.movilidad || perfil.movilidad.length === 0) nuevo.movilidad = 'Seleccione al menos una opción';
    setErrors(nuevo);
    if (Object.keys(nuevo).length > 0) { // Si hay errores, enfocamos el primero
      const ordenCampos = [ // Definimos el orden visual de los campos en el formulario
        'emailPersonal', // primero email arriba
        'contactoNombre',
        'contactoTelefono',
        'escolaridad', // Primero escolaridad
        'movilidad',   // Luego movilidad, ya que quedó debajo de escolaridad en UI
        'direccion',
        'latitud',
        'longitud',
        'ciudad',
        'barrio',
      ];
      const firstKey = ordenCampos.find(k => (nuevo as any)[k]); // Buscamos el primer campo con error
      if (firstKey) {
        setTimeout(() => focusAndScrollTo(firstKey), 0); // Defer para esperar que React pinte los errores
      }
      return false; // No es válido
    }
    return true; // Sin errores
  };

  // Intenta capturar la mejor ubicación posible y prellenar ciudad/barrio/dirección mediante reverse geocoding
  const capturarUbicacion = async () => {
    try {
      setLoadingGeo(true);
      // Obtenemos la mejor posición posible dentro del tiempo límite (15m deseado, 15s máx)
      const pos = await getBestPosition(15, 15000);

      // Validamos contra el backend que la ubicación NO caiga dentro de un PS (>= 50 m).
      // Si cae adentro, abortamos sin setear coordenadas para que el operario reintente desde su domicilio.
      try {
        const validacion = await validarUbicacionPerfil(Number(pos.lat), Number(pos.lng));
        if (!validacion.permitido) {
          const distancia = validacion.distancia_m != null ? `${validacion.distancia_m} m` : 'menos de 50 m';
          setPsAlertMensaje(
            `Estás muy cerca de "${validacion.ps_cercano || 'un Punto de Servicio'}" (${distancia}). ` +
            'Para registrar tu domicilio, realizá la captura desde tu casa.'
          );
          setPsAlertOpen(true);
          return;
        }
      } catch {
        // Si la validación falla (red, backend caído), continuamos: el upsert del servidor
        // hará la validación definitiva y rechazará el guardado si corresponde.
      }

      const tryReverse = async () => {
        const rev = await reverseGeocode(pos.lat, pos.lng);
        let barrioInferido = rev.barrio || '';
        if (!barrioInferido && rev.direccion) {
          try {
            const partes = rev.direccion.split(',').map(s => s.trim());
            const idxCity = rev.ciudad ? partes.findIndex(p => p.toLowerCase() === String(rev.ciudad).toLowerCase()) : -1;
            if (idxCity > 0) barrioInferido = partes[idxCity - 1];
            if (!barrioInferido && partes.length >= 2) barrioInferido = partes[1];
          } catch {}
        }
        return { rev, barrioInferido };
      };

      try {
        // Hasta 3 intentos para evitar respuestas cacheadas/incompletas
        let rev: any = null; let barrioInferido = '';
        for (let i = 0; i < 3; i++) {
          const res = await tryReverse();
          rev = res.rev; barrioInferido = res.barrioInferido;
          if (barrioInferido) break;
          await new Promise(r => setTimeout(r, 300));
        }
        setPerfil(prev => ({
          ...prev,
          latitud: pos.lat,
          longitud: pos.lng,
          ciudad: rev.ciudad || prev.ciudad || '',
          barrio: barrioInferido || prev.barrio || prev.barrio || '', // nunca vaciar si ya había valor
          direccion: prev.direccion || rev.direccion || prev.direccion,
        }));
        // Limpiar errores de los campos autocompletados
        setErrors(prev => ({
          ...prev,
          ciudad: '',
          barrio: '',
          direccion: '',
          latitud: '',
          longitud: ''
        }));
        // Refuerzo: si barrioInferido existe, aplicarlo nuevamente en el siguiente tick para evitar carreras
        if (barrioInferido) {
          setTimeout(() => {
            setPerfil(prev => ({ ...prev, barrio: prev.barrio || barrioInferido }));
          }, 0);
        }
      } catch {
        // Si el reverse geocoding falla, al menos guardamos las coordenadas
        setPerfil(prev => ({ ...prev, latitud: pos.lat, longitud: pos.lng }));
      }
    } catch (e: any) {
      // Mostramos una alerta simple si no se pudo acceder a la ubicación
      alert(e.message || 'No se pudo obtener la ubicación (verifica permisos del navegador)');
    } finally {
      setLoadingGeo(false);
    }
  };

  // Guarda el perfil en localStorage y notifica al padre
  const guardar = async () => {
    if (!validar()) return;
    // Persistimos en backend
    try {
      await putPerfilOperario({
        // incluir correo personal en payload
        emailPersonal: String(perfil.emailPersonal),
        escolaridad: String(perfil.escolaridad),
        ciudad: String(perfil.ciudad),
        barrio: String(perfil.barrio),
        direccion: String(perfil.direccion),
        latitud: Number(perfil.latitud),
        longitud: Number(perfil.longitud),
        contactoNombre: String(perfil.contactoNombre),
        contactoTelefono: String(perfil.contactoTelefono),
        movilidad: Array.isArray(perfil.movilidad) && perfil.movilidad.length > 0 ? perfil.movilidad : ['No'],
      });
    } catch (e: any) {
      // El backend devuelve 422 con codigo=UBICACION_CERCANA_PS cuando la coordenada cae dentro de un PS.
      const datos = e?.response?.data || {};
      if (datos.codigo === 'UBICACION_CERCANA_PS') {
        setPsAlertMensaje(
          datos.error ||
          'No se permite registrar la ubicación dentro de un Punto de Servicio. Realizá la actualización desde tu domicilio.'
        );
        setPsAlertOpen(true);
      } else {
        setPsAlertMensaje(datos.error || 'No se pudo guardar en el servidor. Intentá nuevamente.');
        setPsAlertOpen(true);
      }
      return;
    }
    // Persistimos localmente para el banner actual
    savePerfil(perfil);
    // Avisamos al padre que se guardó (para re-evaluar si se oculta el banner)
    onSaved();
    // Cerramos el modal
    onClose();
  };

  const hasErrors = Object.keys(errors).length > 0;
  // Habilitar Guardar según completitud real del formulario
  const canSave = Boolean(
    perfil.emailPersonal && isValidEmail(String(perfil.emailPersonal)) && // email requerido y válido
    perfil.escolaridad &&
    perfil.ciudad &&
    perfil.barrio &&
    perfil.direccion &&
    perfil.latitud &&
    perfil.longitud &&
    perfil.contactoNombre &&
    perfil.contactoTelefono &&
    perfil.movilidad && perfil.movilidad.length > 0 &&
    isValidPyPhone(`+595 ${perfil.contactoTelefono}`)
  );

  // Render del modal con un encabezado fijo y contenido scrollable
  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose} backdropDismiss={false} cssClass="modal-full"> {/* Modal bloqueante para priorizar la actualización y a pantalla completa en móvil */}
      <IonHeader> {/* Encabezado del modal */}
        <IonToolbar color="primary"> {/* Barra superior con color de marca */}
          <IonTitle>Actualizar mis datos</IonTitle> {/* Título del modal */}
          <IonButtons slot="end">
            {/* Siempre permitimos saltar; el padre decide qué hacer (en rutas bloqueadas redirige a /home). */}
            <IonButton onClick={onClose}>{forzado ? 'Saltar' : 'Cerrar'}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding"> {/* Contenido con padding */}
        {/* Forma de una sola columna, scrolleable */}
        <IonList className="form-vertical"> {/* Lista única con todos los campos en orden vertical */}
          {/* campo nuevo: correo personal arriba de todo */}
          <IonItem data-field="emailPersonal" ref={(el) => { fieldRefs.current['emailPersonal'] = el as unknown as HTMLElement; }}> {/* Email personal requerido */}
            <IonLabel position="stacked">Correo Electrónico Personal *</IonLabel> {/* Campo obligatorio */}
            <div className="field-box"> {/* Caja visual para el input */}
              <IonInput type="email" required={true} ref={refEmailPersonal} value={perfil.emailPersonal || ''} onIonChange={(e) => onChange('emailPersonal', String(e.detail.value))} placeholder="Ej: usuario@gmail.com" /> {/* Input de email */}
            </div>
          </IonItem>
          {errors.emailPersonal && <IonText color="danger" className="ion-padding-start">{errors.emailPersonal}</IonText>}

          <IonItem data-field="contactoNombre" ref={(el) => { fieldRefs.current['contactoNombre'] = el as unknown as HTMLElement; }}> {/* Nombre de contacto de emergencia: actualizamos etiqueta y mantenemos refs para scroll */}
            <IonLabel position="stacked">Nombre de Contacto de Emergencia *</IonLabel> {/* Campo obligatorio (etiqueta actualizada por requerimiento) */}
            <div className="field-box"> {/* Caja visual para el input */}
              <IonInput required={true} ref={refContactoNombre} value={perfil.contactoNombre || ''} onIonChange={(e) => onChange('contactoNombre', String(e.detail.value))} placeholder="Ej: Juan Pérez" /> {/* Marcamos como requerido y añadimos ref para setFocus */}
            </div>
          </IonItem>
          {errors.contactoNombre && <IonText color="danger" className="ion-padding-start">{errors.contactoNombre}</IonText>}

          <IonItem data-field="contactoTelefono" ref={(el) => { fieldRefs.current['contactoTelefono'] = el as unknown as HTMLElement; }}> {/* Teléfono de contacto de emergencia: actualizamos etiqueta y mantenemos data-field */}
            <IonLabel position="stacked">Número de Contacto de Emergencia *</IonLabel> {/* Campo obligatorio (etiqueta actualizada por requerimiento) */}
            <div className="phone-group"> {/* Caja con prefijo a la izquierda */}
              <span className="prefix">(+595)</span> {/* Prefijo fijo */}
              <IonInput
                inputmode="tel"
                required={true}
                ref={refContactoTelefono} // Ref para poder hacer focus programático
                value={perfil.contactoTelefono || ''}
                onIonChange={(e) => onChange('contactoTelefono', formatPyLocal(String(e.detail.value)))}
                placeholder="981 123 456"
              />
            </div>
          </IonItem>
          {errors.contactoTelefono && <IonText color="danger" className="ion-padding-start">{errors.contactoTelefono}</IonText>}

          <IonItem data-field="escolaridad" ref={(el) => { fieldRefs.current['escolaridad'] = el as unknown as HTMLElement; }}> {/* Escolaridad: añadimos data-field y ref para scroll */}
            <IonLabel position="stacked">Escolaridad *</IonLabel> {/* Campo obligatorio */}
            <div className="field-box select-field"> {/* Caja visual también para select */}
              <IonSelect aria-required={true} value={perfil.escolaridad} onIonChange={(e) => onChange('escolaridad', String(e.detail.value))}>
                <IonSelectOption value="Primaria Concluida">Primaria Concluida</IonSelectOption>
                <IonSelectOption value="Secundaria Concluida">Secundaria Concluida</IonSelectOption>
                <IonSelectOption value="Estudiante Terciario">Estudiante Terciario</IonSelectOption>
                <IonSelectOption value="Estudiante Universitario">Estudiante Universitario</IonSelectOption>
                <IonSelectOption value="Universidad Concluida">Universidad Concluida</IonSelectOption>
              </IonSelect>
            </div>
          </IonItem>
          {errors.escolaridad && <IonText color="danger" className="ion-padding-start">{errors.escolaridad}</IonText>}
          <IonItem data-field="movilidad" ref={(el) => { fieldRefs.current['movilidad'] = el as unknown as HTMLElement; }}> {/* Movilidad propia: se reubica debajo de Escolaridad */}
            <IonLabel position="stacked">¿Cuenta con Movilidad Propia? *</IonLabel> {/* Campo obligatorio reubicado */}
            <div className="field-box select-field"> {/* Caja visual para select múltiple */}
              <IonSelect multiple={true} placeholder="Seleccione" value={perfil.movilidad || []} onIonChange={(e) => setPerfil(prev => ({ ...prev, movilidad: e.detail.value as string[] }))}>
                <IonSelectOption value="Moto">Moto</IonSelectOption>
                <IonSelectOption value="Auto">Auto</IonSelectOption>
                <IonSelectOption value="No">No</IonSelectOption>
              </IonSelect>
            </div>
          </IonItem>
          {errors.movilidad && <IonText color="danger" className="ion-padding-start">{errors.movilidad}</IonText>}

          <IonItem lines="none"> {/* Botón obtener ubicación: lo ubicamos antes de Dirección para autocompletar */}
            <IonButton expand="block" size="large" className="btn-geo" onClick={capturarUbicacion} disabled={loadingGeo}>
              {loadingGeo && <IonSpinner name="crescent" slot="start" />} {loadingGeo ? 'Obteniendo ubicación…' : 'Obtener mi ubicación actual'}
            </IonButton>
          </IonItem>
          <IonText color="medium" className="hint">Necesita permiso del navegador</IonText>

          <IonItem data-field="direccion" ref={(el) => { fieldRefs.current['direccion'] = el as unknown as HTMLElement; }}> {/* Dirección: añadimos data-field y ref */}
            <IonLabel position="stacked">Dirección *</IonLabel> {/* Campo obligatorio */}
            <div className="field-box">
              <IonInput required={true} ref={refDireccion} value={perfil.direccion || ''} onIonChange={(e) => onChange('direccion', String(e.detail.value))} placeholder="Calle y número, referencias" /> {/* Añadimos ref para setFocus */}
            </div>
          </IonItem>
          {errors.direccion && <IonText color="danger" className="ion-padding-start">{errors.direccion}</IonText>}

          <IonItem data-field="latitud" ref={(el) => { fieldRefs.current['latitud'] = el as unknown as HTMLElement; }}> {/* Latitud: añadimos data-field y ref */}
            <IonLabel position="stacked">Coordenadas de Latitud *</IonLabel> {/* Campo obligatorio */}
            <div className="field-box">
              <IonInput required={true} ref={refLatitud} value={perfil.latitud || ''} onIonChange={(e) => onChange('latitud', String(e.detail.value))} placeholder="Ej: -25.2967" /> {/* Añadimos ref para setFocus */}
            </div>
          </IonItem>
          {errors.latitud && <IonText color="danger" className="ion-padding-start">{errors.latitud}</IonText>}

          <IonItem data-field="longitud" ref={(el) => { fieldRefs.current['longitud'] = el as unknown as HTMLElement; }}> {/* Longitud: añadimos data-field y ref */}
            <IonLabel position="stacked">Coordenadas de Longitud *</IonLabel> {/* Campo obligatorio */}
            <div className="field-box">
              <IonInput required={true} ref={refLongitud} value={perfil.longitud || ''} onIonChange={(e) => onChange('longitud', String(e.detail.value))} placeholder="Ej: -57.6459" /> {/* Añadimos ref para setFocus */}
            </div>
          </IonItem>
          {errors.longitud && <IonText color="danger" className="ion-padding-start">{errors.longitud}</IonText>}

          <IonItem data-field="ciudad" ref={(el) => { fieldRefs.current['ciudad'] = el as unknown as HTMLElement; }}> {/* Ciudad: añadimos data-field y ref */}
            <IonLabel position="stacked">Ciudad *</IonLabel>
            <div className="field-box">
              <IonInput required={true} ref={refCiudad} value={perfil.ciudad || ''} onIonChange={(e) => onChange('ciudad', String(e.detail.value))} placeholder="Ej: Asunción" /> {/* Añadimos ref para setFocus */}
            </div>
          </IonItem>
          {errors.ciudad && <IonText color="danger" className="ion-padding-start">{errors.ciudad}</IonText>}

          <IonItem data-field="barrio" ref={(el) => { fieldRefs.current['barrio'] = el as unknown as HTMLElement; }}> {/* Barrio: añadimos data-field y ref */}
            <IonLabel position="stacked">Barrio *</IonLabel>
            <div className="field-box">
              <IonInput required={true} ref={refBarrio} value={perfil.barrio || ''} onIonChange={(e) => onChange('barrio', String(e.detail.value))} placeholder="Ej: San Cristóbal" /> {/* Añadimos ref para setFocus */}
            </div>
          </IonItem>
          {errors.barrio && <IonText color="danger" className="ion-padding-start">{errors.barrio}</IonText>}

          <IonItem lines="none" className="ion-margin-top"> {/* Acciones */}
            <IonButton color="primary" size="large" className="btn-guardar" onClick={guardar} disabled={!canSave}>Guardar</IonButton>
          </IonItem>
        </IonList>

        {/* Alerta interna estilo Ionic para validación de ubicación (proximidad a un PS) y otros errores de guardado */}
        <IonAlert
          isOpen={psAlertOpen}
          onDidDismiss={() => setPsAlertOpen(false)}
          header="Ubicación no válida"
          message={psAlertMensaje}
          buttons={['Entendido']}
        />
      </IonContent>
    </IonModal>
  );
};

// Exportamos por defecto para que sea fácil importarlo desde Home
export default ActualizarDatosModal;
